import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Info, Search, X } from 'lucide-react'
import { tokens } from '@/theme'
import type {
  Capability,
  Domain,
  InitiativeListItem,
  LookupBusinessUnit,
  PaginatedResponse,
} from '@/domain/types'
import type { QueryState } from '@/api/useApiQuery'
import { useDomains } from '@/api/hooks/useDomains'
import { useCapabilities } from '@/api/hooks/useCapabilities'
import { useInitiatives } from '@/api/hooks/useInitiatives'
import { useLookupBusinessUnits } from '@/api/hooks/useLookups'
import { OrionScene } from '@/components/visual/OrionScene'
import { StagePill } from '@/components/StagePill'
import { SkeletonList, ErrorPanel } from '@/components/feedback'

/**
 * Business architecture — the capability map.
 *
 * Implements `Capabilities v2.dc.html`. Three panes, one screen: pick a
 * domain on the left, see the capabilities it owns in the middle, read the
 * initiatives strengthening the selected one on the right.
 *
 * What v2 changed from the first cut of this page:
 *
 *   - **The hero collapsed into a band** carrying four portfolio counts —
 *     domains, capabilities, initiatives, and the one that earns its place,
 *     how many capabilities no initiative touches at all. The map's whole
 *     argument is "investment traces to business capability", and the gaps
 *     are the half of that argument the old hero never showed.
 *   - **Descriptions moved into the selection.** Domain and capability rows
 *     are name-only until selected, then they open. The old page printed
 *     every capability's description on a card at once, which made a domain
 *     of four capabilities a wall of prose to skim.
 *   - **The right pane filters.** A capability with a dozen initiatives on
 *     it needed a way to find one; search and a business-unit filter narrow
 *     the list in place, with a count of what's hidden.
 *
 * Two deliberate departures from the design file:
 *
 *   - **The business-unit filter is a native `<select>`**, not the design's
 *     custom dropdown. Same control, same visual weight, and it comes with
 *     keyboard and screen-reader behaviour the custom one would have to
 *     re-earn — the initiatives filter bar made the same call.
 *   - **Status pills come from `StagePill`**, so a Pilot here is the same
 *     colour as a Pilot on the initiatives list and the detail view. The
 *     design's own four-colour map is a mock convenience.
 *
 * Selection lives in component state rather than the URL, so a selected
 * capability isn't shareable — the per-capability permalink is still
 * `/capabilities/{id}`, which the initiative detail view links to.
 */

interface Props {
  onSelectInitiative: (initiativeId: number) => void
  /** Routes to chat with a prompt seeded — the empty-capability CTA. */
  onProposeInitiative: (capabilityName: string) => void
  /** Hands chat a seeded question — the hero CTA. */
  onRaiseIdea: (question: string) => void
}

/** Domains and capabilities are small, bounded sets; one page holds them all. */
const PAGE_SIZE = 100

const RAISE_IDEA_PROMPT =
  'I have an idea for an AI initiative — help me shape it into something the funnel can assess.'

const explorerStyles = `
.orion-cap-grid {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;
}
@media (max-width: 1180px) {
  .orion-cap-grid { grid-template-columns: 260px minmax(0, 1fr); }
  .orion-cap-detail { grid-column: 1 / -1; position: static !important; }
}
@media (max-width: 820px) {
  .orion-cap-grid { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 640px) {
  .orion-cap-glyph { display: none; }
}
`

export function CapabilitiesView({ onSelectInitiative, onProposeInitiative, onRaiseIdea }: Props) {
  const [domainId, setDomainId] = useState<number | null>(null)
  const [capabilityId, setCapabilityId] = useState<number | null>(null)
  const [q, setQ] = useState('')
  const [businessUnitCode, setBusinessUnitCode] = useState<string | 'all'>('all')

  const domainsQuery = useDomains({ page: 1, pageSize: PAGE_SIZE })
  const domains = useMemo(
    () => (domainsQuery.status === 'success' ? domainsQuery.data.data : []),
    [domainsQuery],
  )

  // Portfolio-wide, for the hero counts only. Separate from the pane query
  // below so the map stays complete for the selected domain even if the
  // whole tree ever outgrows one page.
  const allCapabilitiesQuery = useCapabilities({ page: 1, pageSize: PAGE_SIZE })
  const businessUnitsQuery = useLookupBusinessUnits()

  // Default to the first domain once the list lands. Guarded on `domainId`
  // being unset so it never fights a user selection.
  useEffect(() => {
    if (domainId === null && domains.length > 0) setDomainId(domains[0]!.domainId)
  }, [domainId, domains])

  const capabilitiesQuery = useCapabilities(
    domainId === null ? { page: 1, pageSize: 1 } : { page: 1, pageSize: PAGE_SIZE, domainId },
  )

  // The middle pane shows level-1 capabilities — the domain's own children.
  // Level 2 hangs off those and belongs on the detail page.
  const capabilities = useMemo(() => {
    if (domainId === null || capabilitiesQuery.status !== 'success') return []
    return capabilitiesQuery.data.data.filter(c => c.capabilityLevel === 1)
  }, [capabilitiesQuery, domainId])

  // Keep the selected capability inside the selected domain: after a domain
  // change the old selection is stale, so fall back to the first.
  useEffect(() => {
    if (capabilities.length === 0) return
    if (capabilities.some(c => c.capabilityId === capabilityId)) return
    setCapabilityId(capabilities[0]!.capabilityId)
  }, [capabilities, capabilityId])

  const domain = domains.find(d => d.domainId === domainId) ?? null
  const capability = capabilities.find(c => c.capabilityId === capabilityId) ?? null

  return (
    <>
      <style>{explorerStyles}</style>

      <CapabilityHero
        counts={portfolioCounts(domainsQuery, allCapabilitiesQuery)}
        onRaiseIdea={() => onRaiseIdea(RAISE_IDEA_PROMPT)}
      />

      <WhatIsACapability />

      <div
        className="orion-cap-grid orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          width: '100%',
          margin: '0 auto',
          padding: `26px ${tokens.containerPadX}px 72px`,
          boxSizing: 'border-box',
        }}
      >
        {/* ————— Domains ————— */}
        <div>
          <PaneHeader label="Domain" />
          {(domainsQuery.status === 'idle' || domainsQuery.status === 'loading') && (
            <SkeletonList rows={8} rowHeight={46} />
          )}
          {domainsQuery.status === 'error' && (
            <ErrorPanel error={domainsQuery.error} maxWidth="100%" />
          )}
          {domainsQuery.status === 'success' && (
            <ul style={{ listStyle: 'none', display: 'grid', gap: 4, margin: 0, padding: 0 }}>
              {domains.map(d => (
                <li key={d.domainId}>
                  <DomainRow
                    domain={d}
                    active={d.domainId === domainId}
                    onClick={() => setDomainId(d.domainId)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ————— Capabilities in the selected domain ————— */}
        <div>
          <PaneHeader label="Capabilities" aside={domain ? `in ${domain.domainName}` : undefined} />

          {(capabilitiesQuery.status === 'idle' || capabilitiesQuery.status === 'loading') && (
            <SkeletonList rows={4} rowHeight={52} />
          )}
          {capabilitiesQuery.status === 'error' && (
            <ErrorPanel error={capabilitiesQuery.error} maxWidth="100%" />
          )}
          {capabilitiesQuery.status === 'success' && capabilities.length === 0 && (
            <p
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 13,
                color: tokens.ink3,
                background: tokens.paper2,
                border: `1px solid ${tokens.line}`,
                padding: '20px 22px',
                margin: 0,
              }}
            >
              This domain has no capabilities mapped yet.
            </p>
          )}
          {capabilities.length > 0 && (
            <ul style={{ listStyle: 'none', display: 'grid', gap: 4, margin: 0, padding: 0 }}>
              {capabilities.map(c => (
                <li key={c.capabilityId}>
                  <CapabilityRow
                    capability={c}
                    active={c.capabilityId === capabilityId}
                    onClick={() => setCapabilityId(c.capabilityId)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ————— Initiatives on the selected capability ————— */}
        <div className="orion-cap-detail" style={{ position: 'sticky', top: 92 }}>
          <PaneHeader label="Initiatives" />
          <InitiativePanel
            capability={capability}
            domainName={domain?.domainName ?? ''}
            q={q}
            onQ={setQ}
            businessUnitCode={businessUnitCode}
            onBusinessUnitCode={setBusinessUnitCode}
            businessUnits={businessUnitsQuery.status === 'success' ? businessUnitsQuery.data : []}
            onSelectInitiative={onSelectInitiative}
            onProposeInitiative={onProposeInitiative}
          />
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Hero — the map, and the four numbers that describe it
// ---------------------------------------------------------------------------

interface PortfolioCounts {
  domains: number | null
  capabilities: number | null
  initiatives: number | null
  uncovered: number | null
}

/**
 * Counts for the hero band.
 *
 * `initiatives` sums the per-domain counts rather than asking the stats
 * endpoint: every initiative hangs off a capability, so the domain totals
 * already add up to the portfolio, and it costs no extra request.
 *
 * The two capability numbers describe **level 1** — the population the map
 * itself lists — and `uncovered` rolls level-2 work up to its parent. A
 * capability whose sub-capability carries the initiative is covered; saying
 * otherwise would inflate the gap count for any org that maps initiatives
 * to the finer level.
 *
 * Both need `initiativeCount` on every capability, so they are only honest
 * while the whole tree fits one page — past that they read "—" rather than
 * counting a subset and calling it the total.
 */
function portfolioCounts(
  domainsQuery: QueryState<PaginatedResponse<Domain>>,
  capabilitiesQuery: QueryState<PaginatedResponse<Capability>>,
): PortfolioCounts {
  const domainsOk = domainsQuery.status === 'success'
  const capsComplete = capabilitiesQuery.status === 'success' && !capabilitiesQuery.data.hasMore

  let capabilities: number | null = null
  let uncovered: number | null = null

  if (capsComplete) {
    const rows = capabilitiesQuery.data.data
    const rolledUp = new Map<number, number>()
    for (const c of rows) {
      const owner = c.capabilityLevel === 1 ? c.capabilityId : c.parentCapabilityId
      if (owner === null) continue
      rolledUp.set(owner, (rolledUp.get(owner) ?? 0) + c.initiativeCount)
    }
    const levelOne = rows.filter(c => c.capabilityLevel === 1)
    capabilities = levelOne.length
    uncovered = levelOne.filter(c => (rolledUp.get(c.capabilityId) ?? 0) === 0).length
  }

  return {
    domains: domainsOk ? domainsQuery.data.total : null,
    capabilities,
    initiatives:
      domainsOk && !domainsQuery.data.hasMore
        ? domainsQuery.data.data.reduce((sum, d) => sum + d.initiativeCount, 0)
        : null,
    uncovered,
  }
}

function CapabilityHero({
  counts,
  onRaiseIdea,
}: {
  counts: PortfolioCounts
  onRaiseIdea: () => void
}) {
  return (
    <section
      style={{
        position: 'relative',
        background: tokens.inkPanel,
        borderBottom: `1px solid ${tokens.inkPanelLine}`,
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 32% 60%, rgba(153,130,66,0.20) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="orion-pad"
        style={{
          position: 'relative',
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `16px ${tokens.containerPadX}px`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 26,
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: '0 1 auto' }}>
          <div className="orion-cap-glyph" style={{ width: 132, flex: 'none' }}>
            <OrionScene variant="graph" ink="light" height={104} />
          </div>

          <div>
            <div
              style={{
                width: 32,
                height: 2,
                background: tokens.gold,
                marginBottom: 10,
                transformOrigin: 'left',
                animation: `barGrow ${tokens.durSlow} ${tokens.easeStandard} both`,
              }}
            />
            <div
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 10,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: tokens.inkPanelText2,
                marginBottom: 8,
              }}
            >
              Business Architecture
            </div>
            <h1
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 26,
                fontWeight: tokens.weightDemi,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: tokens.paper,
                margin: 0,
              }}
            >
              <span style={{ opacity: 0.5 }}>The capability map </span>behind every initiative.
            </h1>
            <p
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 12.5,
                color: tokens.inkPanelText2,
                margin: '6px 0 0',
                maxWidth: '44ch',
              }}
            >
              Pick a domain, then a capability. The initiatives strengthening it appear on the
              right.
            </p>
          </div>
        </div>

        {/* Named as a group: four bare numbers in a row mean nothing read
            one at a time, and "12 No coverage" needs to say what it is a
            count of. */}
        <div
          role="group"
          aria-label="The capability map in numbers"
          style={{ flex: '0 1 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}
        >
          <HeroCount label="Domains" value={counts.domains} first />
          <HeroCount label="Capabilities" value={counts.capabilities} />
          <HeroCount label="Initiatives" value={counts.initiatives} />
          <HeroCount label="No coverage" value={counts.uncovered} />
        </div>

        <RaiseIdeaButton onClick={onRaiseIdea} />
      </div>
    </section>
  )
}

/**
 * One hero statistic. The first carries the gold rule; the rest are divided
 * by a hairline, which is the design's way of saying "domains is the axis
 * the other three hang off".
 */
function HeroCount({
  label,
  value,
  first,
}: {
  label: string
  value: number | null
  first?: boolean
}) {
  return (
    <div
      style={{
        padding: first ? '8px 18px 8px 0' : '8px 18px',
        borderLeft: first ? 'none' : `1px solid ${tokens.inkPanel2}`,
        borderBottom: first ? `2px solid ${tokens.gold}` : 'none',
        marginBottom: -1,
      }}
    >
      <div
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 21,
          fontWeight: tokens.weightDemi,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: first ? tokens.paper : tokens.inkPanelText3,
        }}
      >
        {value === null ? '—' : value}
      </div>
      <div
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.inkPanelText2,
          marginTop: 6,
        }}
      >
        {label}
      </div>
    </div>
  )
}

function RaiseIdeaButton({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        background: 'transparent',
        border: `1px solid ${hover ? tokens.gold : tokens.inkPanelLine}`,
        borderRadius: tokens.radiusMd,
        padding: '9px 16px',
        fontFamily: tokens.fontDisplay,
        fontSize: 11,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: hover ? tokens.paper : tokens.inkPanelText,
        cursor: 'pointer',
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      Raise an idea
      <ChevronRight size={12} strokeWidth={2} color={tokens.gold} />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Explainer strip
// ---------------------------------------------------------------------------

function WhatIsACapability() {
  return (
    <div style={{ background: tokens.paper2, borderBottom: `1px solid ${tokens.line}` }}>
      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `14px ${tokens.containerPadX}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <Info size={17} strokeWidth={1.6} color={tokens.gold} style={{ flexShrink: 0 }} />
        <p
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: tokens.ink2,
            margin: 0,
          }}
        >
          <strong style={{ fontWeight: tokens.weightDemi }}>What is a capability?</strong> Something
          the business does — quoting, forecasting, maintaining equipment — stable over time and
          independent of org charts or systems. Every AI initiative is anchored to the capability it
          strengthens.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Panes
// ---------------------------------------------------------------------------

/**
 * The gold rule and label above each pane. Marked up as an `h2` — three
 * unlabelled columns are three unlabelled columns to a screen reader, and
 * these are the only names they have.
 */
function PaneHeader({ label, aside }: { label: string; aside?: string | undefined }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 14,
      }}
    >
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
        <span style={{ width: 20, height: 2, background: tokens.gold }} />
        <span
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 10.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: tokens.gold,
          }}
        >
          {label}
        </span>
      </h2>
      {aside && (
        <span style={{ fontFamily: tokens.fontDisplay, fontSize: 11, color: tokens.ink4 }}>
          {aside}
        </span>
      )}
    </div>
  )
}

/** The selected capability, named where a screen reader reads the pane. */
function Breadcrumb({
  domainName,
  capabilityName,
}: {
  domainName: string
  capabilityName: string
}) {
  return (
    <div
      style={{
        fontFamily: tokens.fontDisplay,
        fontSize: 11.5,
        color: tokens.ink3,
        marginBottom: 12,
      }}
    >
      {domainName} › {capabilityName}
    </div>
  )
}

/**
 * A row in either of the two selector panes.
 *
 * Selected rows invert to the ink panel and open their description; the
 * unselected ones stay a name on paper. One row is expanded at a time in
 * each pane, so a domain of a dozen capabilities is still a list you can
 * read down rather than a page of prose.
 */
function SelectorRow({
  name,
  description,
  active,
  emphasis,
  onClick,
}: {
  name: string
  description: string | null
  active: boolean
  /** Middle pane sits one step louder than the left — it is the choice. */
  emphasis: 'domain' | 'capability'
  onClick: () => void
}) {
  const [hover, setHover] = useState(false)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: active ? tokens.inkPanel : hover ? tokens.paper2 : tokens.paper,
        border: `1px solid ${active ? tokens.inkPanel : hover ? tokens.line2 : tokens.line}`,
        padding: emphasis === 'domain' ? '13px 16px' : '15px 18px',
        cursor: 'pointer',
        fontFamily: tokens.fontDisplay,
        transition: `background ${tokens.durFast} ${tokens.easeStandard}, border-color ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: emphasis === 'domain' ? 14 : 15,
          fontWeight: tokens.weightDemi,
          lineHeight: 1.3,
          color: active ? tokens.paper : emphasis === 'domain' ? tokens.ink2 : tokens.ink,
        }}
      >
        {name}
      </span>
      {active && description && (
        <span
          style={{
            display: 'block',
            fontSize: emphasis === 'domain' ? 12 : 12.5,
            lineHeight: 1.6,
            fontWeight: tokens.weightBook,
            color: tokens.inkPanelText,
            marginTop: emphasis === 'domain' ? 9 : 10,
          }}
        >
          {description}
        </span>
      )}
    </button>
  )
}

function DomainRow({
  domain,
  active,
  onClick,
}: {
  domain: Domain
  active: boolean
  onClick: () => void
}) {
  return (
    <SelectorRow
      name={domain.domainName}
      description={domain.domainDescription}
      active={active}
      emphasis="domain"
      onClick={onClick}
    />
  )
}

function CapabilityRow({
  capability,
  active,
  onClick,
}: {
  capability: Capability
  active: boolean
  onClick: () => void
}) {
  return (
    <SelectorRow
      name={capability.capabilityName}
      description={capability.capabilityDescription}
      active={active}
      emphasis="capability"
      onClick={onClick}
    />
  )
}

// ---------------------------------------------------------------------------
// Initiatives on the selected capability
// ---------------------------------------------------------------------------

interface PanelProps {
  capability: Capability | null
  domainName: string
  q: string
  onQ: (next: string) => void
  businessUnitCode: string | 'all'
  onBusinessUnitCode: (next: string | 'all') => void
  businessUnits: LookupBusinessUnit[]
  onSelectInitiative: (initiativeId: number) => void
  onProposeInitiative: (capabilityName: string) => void
}

function InitiativePanel(props: PanelProps) {
  const { capability } = props

  // Hooks can't be conditional, so before a capability is selected (first
  // paint only) this issues a minimal request rather than skipping.
  const query = useInitiatives(
    capability === null
      ? { page: 1, pageSize: 1 }
      : { page: 1, pageSize: PAGE_SIZE, capabilityId: capability.capabilityId },
  )
  const all = capability !== null && query.status === 'success' ? query.data.data : []

  // Filtering is client-side: the set is one capability's initiatives, it
  // arrived in one page, and narrowing it here is what makes "3 of 11
  // shown" answerable at all — a server-filtered list can only report what
  // survived, never what it hid.
  const needle = props.q.trim().toLowerCase()
  const shown = all.filter(
    i =>
      (props.businessUnitCode === 'all' || i.businessUnitCode === props.businessUnitCode) &&
      (needle === '' ||
        i.initiativeName.toLowerCase().includes(needle) ||
        i.ownerDisplayName.toLowerCase().includes(needle)),
  )
  const filtered = props.q.trim() !== '' || props.businessUnitCode !== 'all'

  return (
    <div>
      {capability ? (
        <Breadcrumb domainName={props.domainName} capabilityName={capability.capabilityName} />
      ) : (
        <Breadcrumb domainName={props.domainName || '—'} capabilityName="—" />
      )}

      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        <FilterInput value={props.q} onChange={props.onQ} />

        <select
          aria-label="Filter by business unit"
          value={props.businessUnitCode === 'all' ? '' : props.businessUnitCode}
          onChange={e => props.onBusinessUnitCode(e.currentTarget.value || 'all')}
          disabled={props.businessUnits.length === 0}
          style={{
            width: '100%',
            padding: '8px 11px',
            fontFamily: tokens.fontDisplay,
            fontSize: 12,
            color: tokens.ink,
            background: tokens.paper,
            border: `1px solid ${props.businessUnitCode === 'all' ? tokens.line2 : tokens.gold}`,
            borderRadius: tokens.radiusMd,
            cursor: props.businessUnits.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <option value="">All business units</option>
          {props.businessUnits.map(bu => (
            <option key={bu.businessUnitCode} value={bu.businessUnitCode}>
              {bu.businessUnitName}
            </option>
          ))}
        </select>

        <div
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 11,
            color: tokens.ink4,
            minHeight: 16,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {shown.length === all.length ? '' : `${shown.length} of ${all.length} shown`}
        </div>
      </div>

      {(query.status === 'idle' || query.status === 'loading') && (
        <SkeletonList rows={2} rowHeight={78} />
      )}
      {query.status === 'error' && <ErrorPanel error={query.error} maxWidth="100%" />}

      {query.status === 'success' && shown.length > 0 && (
        <ul style={{ listStyle: 'none', display: 'grid', gap: 6, margin: 0, padding: 0 }}>
          {shown.map(i => (
            <li key={i.initiativeId}>
              <InitiativeCard
                initiative={i}
                onClick={() => props.onSelectInitiative(i.initiativeId)}
              />
            </li>
          ))}
        </ul>
      )}

      {query.status === 'success' && shown.length === 0 && capability !== null && (
        <EmptyPanel
          filtered={filtered && all.length > 0}
          capabilityName={capability.capabilityName}
          onProposeInitiative={props.onProposeInitiative}
        />
      )}
    </div>
  )
}

function FilterInput({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const [focus, setFocus] = useState(false)
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: `1px solid ${focus ? tokens.gold : tokens.line2}`,
        borderRadius: tokens.radiusMd,
        padding: '8px 11px',
        background: tokens.paper,
        transition: `border-color ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <Search size={13} strokeWidth={1.8} color={tokens.ink4} style={{ flex: 'none' }} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.currentTarget.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        placeholder="Filter initiatives…"
        aria-label="Filter initiatives"
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          color: tokens.ink,
          background: 'transparent',
        }}
      />
      {value !== '' && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear the initiative filter"
          style={{
            flex: 'none',
            display: 'inline-flex',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: tokens.ink4,
          }}
        >
          <X size={13} strokeWidth={1.8} />
        </button>
      )}
    </label>
  )
}

function InitiativeCard({
  initiative,
  onClick,
}: {
  initiative: InitiativeListItem
  onClick: () => void
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: hover ? tokens.paper2 : tokens.paper,
        border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
        padding: '15px 17px',
        cursor: 'pointer',
        fontFamily: tokens.fontDisplay,
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: 13.5,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          lineHeight: 1.4,
        }}
      >
        {initiative.initiativeName}
      </span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 10,
        }}
      >
        <StagePill
          stageCode={initiative.stageCode}
          stageName={initiative.stageName}
          lifecycle={initiative.lifecycle}
        />
        <span style={{ fontSize: 11, color: tokens.ink4 }}>{initiative.ownerDisplayName}</span>
      </span>
      <span style={{ display: 'block', fontSize: 11, color: tokens.ink4, marginTop: 7 }}>
        {initiative.businessUnitName}
      </span>
    </button>
  )
}

/**
 * Nothing to show, for one of two very different reasons: the filter hid
 * everything, or no one has proposed anything here. The second is the one
 * worth acting on, so only it offers the hand-off to chat.
 */
function EmptyPanel({
  filtered,
  capabilityName,
  onProposeInitiative,
}: {
  filtered: boolean
  capabilityName: string
  onProposeInitiative: (capabilityName: string) => void
}) {
  if (filtered) {
    return (
      <div
        style={{
          border: `1px dashed ${tokens.line2}`,
          padding: 22,
          textAlign: 'center',
          fontFamily: tokens.fontDisplay,
          fontSize: 12.5,
          color: tokens.ink3,
        }}
      >
        No initiatives match this filter.
      </div>
    )
  }

  return (
    <div
      style={{
        border: `1px dashed ${tokens.line2}`,
        padding: 26,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 13,
          color: tokens.ink3,
          lineHeight: 1.6,
        }}
      >
        No initiatives are strengthening this capability yet.
      </div>
      <button
        type="button"
        onClick={() => onProposeInitiative(capabilityName)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          marginTop: 14,
          padding: 0,
          background: 'transparent',
          border: 'none',
          fontFamily: tokens.fontDisplay,
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.gold,
          cursor: 'pointer',
        }}
      >
        Propose one in chat
        <ChevronRight size={13} strokeWidth={1.8} />
      </button>
    </div>
  )
}
